import React, { useState, useEffect } from 'react';
import ToggleButton from '../../common/ui/ToggleButton';
import PermissionDenied from '../../common/PermissionDenied';
import { checkProfilerPermission } from '../../common/utils/permissions';
import { ParentContentCardMultiHeader } from '../common/ParentContentCardMultiHeader';
import CustomLoader from '../common/CustomLoader';
import Swal from 'sweetalert2';
import moment from 'moment';
import CustomReactPaginate from '../../common/custom/CustomReactPaginate';

const ProfilerSection = ({
  enableProfiling,
  handleToggleProfiler,
  projectId,
  actions,
  version,
  profilersList = {},
  getProfilerDataPending,
  isReadOnly,
}) => {
  const isAllowed = checkProfilerPermission();
  if (!isAllowed) {
    return <PermissionDenied message="Profiler" />;
  }
  const { totalItems, totalPages, data = [] } = profilersList;
  const [page, setPage] = useState(1);
  const limit = 10;
  useEffect(() => {
    actions.getProfilerData({ projectId, version, page: 1, limit: 10 });
  }, []);

  const onPageChange = async ({ selected }) => {
    let currentPage = selected + 1;
    setPage(currentPage);
    await actions.getProfilerData({ projectId, version, page: currentPage, limit });
  };

  const triggerLoadProfiler = async () => {
    setPage(1);
    await actions.getProfilerData({ projectId, version, page: 1, limit });
  };

  const resetProfilerData = async () => {
    if (profilersList.length === 0) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete all profiler logs.',
      icon: 'warning',
      confirmButtonColor: '#d30b0b',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      await actions.deleteProfiler({ projectId });
      await triggerLoadProfiler();
    }
  };

  return (
    <div className="col-12 drapcode">
      <div className="row mx-auto">
        <ParentContentCardMultiHeader
          isEnvironment={enableProfiling}
          title="Profiler"
          bgColor="drapcode-violet"
          icon="profiler"
          projectId={projectId}
          version={version}
          refreshMethod={triggerLoadProfiler}
          CtaComponent={
            <div className="d-flex align-items-center">
              {enableProfiling && (
                <>
                  <button
                    className="btn btn-theme-delete btn-xs me-2 mb-4 m-1"
                    onClick={isReadOnly ? undefined : resetProfilerData}
                    title={isReadOnly ? 'Read-Only Mode' : 'Delete Profiler'}
                    disabled={isReadOnly}
                  >
                    <i className="wb webconnect-trash-regular"></i>
                  </button>
                  <button
                    className="btn btn-default btn-xs mb-4 m-1"
                    onClick={triggerLoadProfiler}
                    title="Refresh Profiler"
                  >
                    <i className="wb webconnect-refresh"></i>
                  </button>
                </>
              )}
              <ToggleButton
                name="enableProfiling"
                title="Enable/Disable Profiler"
                text={`Profiler ${enableProfiling ? 'Enabled' : 'Disabled'}`}
                onChange={isReadOnly ? undefined : handleToggleProfiler}
                isChecked={enableProfiling}
                isDisabled={isReadOnly}
              />
            </div>
          }
        >
          {enableProfiling && data.length > 0 ? (
            getProfilerDataPending ? (
              <div className="text-center">
                <CustomLoader loading size="20px" color="#303453" />
              </div>
            ) : (
              <>
                <div className="table-responsive-sm show_scrollbar mb-3 mt-5">
                  <table className="table table-bordered table-striped text-center p-3">
                    <thead>
                      <tr>
                        <th>Step</th>
                        <th>Description</th>
                        <th>Meta Data</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map(
                        ({ _id, step, message, meta, startTime, endTime = '', duration }) => (
                          <tr key={_id}>
                            <td>{step}</td>
                            <td>{message}</td>
                            <td>{JSON.stringify(meta, null, 2)}</td>
                            <td>
                              {startTime
                                ? moment(Number(startTime)).format('Do MMM YYYY, h:mm A')
                                : '-'}
                            </td>
                            <td>
                              {endTime
                                ? moment(Number(endTime)).format('Do MMM YYYY, h:mm A')
                                : '-'}
                            </td>
                            <td>{duration ? (duration / 1000).toFixed(2) + ' s' : '-'}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="col-12">
                  {totalItems > limit && (
                    <CustomReactPaginate
                      totalPages={totalPages}
                      pageRangeDisplayed={2}
                      marginPagesDisplayed={1}
                      page={page - 1}
                      onPageChange={onPageChange}
                    />
                  )}
                </div>
              </>
            )
          ) : (
            <div className="alert text-center mt-4">No Profiler Logs found!</div>
          )}
        </ParentContentCardMultiHeader>
      </div>
    </div>
  );
};

export default ProfilerSection;
